import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Search, Plus, Menu, Sun, Moon, Monitor, ChevronDown, Building2, LogOut,
    UserCircle2, Check, Globe, Settings, ShieldCheck, UserPlus, Users, Wallet,
    ScrollText, ClipboardCheck, LifeBuoy,
} from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./GlobalSearch";
import { OwnerTenantList } from "./OwnerTenantList";
import { TopbarWhatsApp } from "@/components/whatsapp/TopbarWhatsApp";

type QuickAction = { to: string; labelKey: string; icon: React.ComponentType<{ size?: number }>; perm?: string };

const QUICK_ACTIONS: QuickAction[] = [
    { to: "/people",           labelKey: "people.first_name",       icon: UserPlus,       perm: "people.create" },
    { to: "/groups",           labelKey: "nav.groups",              icon: Users,          perm: "group.manage" },
    { to: "/finance/invoices", labelKey: "finance.invoice.new",     icon: ScrollText,     perm: "finance.create" },
    { to: "/finance/payments", labelKey: "finance.payment.new",     icon: Wallet,         perm: "finance.create" },
    { to: "/tasks",            labelKey: "tasks.new",               icon: ClipboardCheck, perm: "tasks.create" },
    { to: "/support",          labelKey: "nav.support",             icon: LifeBuoy,       perm: "support.use" },
];

export function Topbar({ onMobileMenu }: { onMobileMenu: () => void }) {
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const { me, activeMembership, switchTenant, exitTenant, logout, hasPerm } = useAuth();
    const navigate = useNavigate();

    const [tenantMenu, setTenantMenu] = useState(false);
    const [profileMenu, setProfileMenu] = useState(false);
    const [quickMenu, setQuickMenu] = useState(false);
    const tenantRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const quickRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (!tenantRef.current?.contains(e.target as Node)) setTenantMenu(false);
            if (!profileRef.current?.contains(e.target as Node)) setProfileMenu(false);
            if (!quickRef.current?.contains(e.target as Node)) setQuickMenu(false);
        };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, []);

    const cycleTheme = () => { const order = ["dark","light","system"] as const; setTheme(order[(order.indexOf(theme)+1)%order.length]); };
    const cycleLang = () => {
        const order = ["az","en","ru"] as const;
        const cur = i18n.language.startsWith("ru") ? "ru" : i18n.language.startsWith("en") ? "en" : "az";
        i18n.changeLanguage(order[(order.indexOf(cur)+1)%order.length]);
    };
    const currentLangLabel = i18n.language.startsWith("ru") ? "RU" : i18n.language.startsWith("en") ? "EN" : "AZ";
    const ThemeIcon = theme === "light" ? Sun : theme === "system" ? Monitor : Moon;

    const memberships = me?.memberships ?? [];
    const isPO = !!me?.user?.is_platform_owner;
    // Platform owner has no memberships; still show the switcher as an entry
    // point into the platform admin panel so the button is never dead.
    const showTenantBtn = memberships.length > 0 || isPO;
    const visibleQuick = QUICK_ACTIONS.filter((a) => !a.perm || hasPerm(a.perm));

    return (
        <header data-testid="topbar" className="sticky top-0 z-30 flex items-center gap-3 px-4 md:px-6 border-b" style={{ height: 64, background: "var(--menu)", borderColor: "var(--border)" }}>
            <button data-testid="mobile-menu-btn" onClick={onMobileMenu} className="md:hidden inline-flex items-center justify-center rounded-md" style={{ width: 44, height: 44, color: "var(--text-muted)" }} aria-label="menu"><Menu size={20} /></button>

            {showTenantBtn && (
                <div ref={tenantRef} className="relative">
                    <button data-testid="tenant-switcher-btn" onClick={() => setTenantMenu((v) => !v)} className="hidden sm:flex items-center gap-2 rounded-md px-3 py-2 text-sm border transition-colors" style={{ color: "var(--text)", background: "var(--input)", borderColor: "var(--border)", minHeight: 40 }}>
                        {isPO && !activeMembership ? <Globe size={16} /> : <Building2 size={16} />}
                        <span className="truncate max-w-[200px]">{activeMembership?.tenant.name || (isPO ? t("nav.platform") : t("topbar.tenant_switcher"))}</span>
                        <ChevronDown size={14} className="opacity-70" />
                    </button>
                    {tenantMenu && (
                        <div className="absolute left-0 top-11 z-40 rounded-md border shadow-xl min-w-[260px] py-1" style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="tenant-menu">
                            {isPO && (
                                <button data-testid="tenant-option-platform" onClick={async () => { setTenantMenu(false); if (activeMembership) await exitTenant(); navigate("/platform"); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left border-b" style={{ color: "var(--text)", borderColor: "var(--border)" }}>
                                    <Globe size={14} style={{ color: "var(--accent)" }} />
                                    <span className="flex-1 truncate font-medium">{t("nav.platform")}</span>
                                </button>
                            )}
                            {isPO ? <OwnerTenantList onClose={() => setTenantMenu(false)} /> : memberships.map((m) => (
                                <button key={m.tenant.id} data-testid={`tenant-option-${m.tenant.id}`} onClick={async () => { setTenantMenu(false); await switchTenant(m.tenant.id); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left" style={{ color: "var(--text)" }}>
                                    <Building2 size={14} style={{ color: "var(--text-muted)" }} />
                                    <span className="flex-1 truncate">{m.tenant.name}{m.tenant.is_demo && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-md" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>DEMO</span>}</span>
                                    {m.tenant.id === activeMembership?.tenant.id && <Check size={14} style={{ color: "var(--primary)" }} />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <GlobalSearch />

            {visibleQuick.length > 0 && (
                <div ref={quickRef} className="relative">
                    <button data-testid="quick-create-btn" onClick={() => setQuickMenu((v) => !v)} className="hidden sm:inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--primary-hover)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--primary)")}>
                        <Plus size={16} /><span className="hidden md:inline">{t("topbar.quick_create")}</span>
                    </button>
                    {quickMenu && (
                        <div className="absolute right-0 top-11 z-40 rounded-md border shadow-xl min-w-[240px] py-1" style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="quick-create-menu">
                            {visibleQuick.map((a) => {
                                const AIcon = a.icon;
                                return (
                                    <button key={a.to} data-testid={`quick-action-${a.labelKey.replace(/\./g, "-")}`} onClick={() => { setQuickMenu(false); navigate(a.to); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left" style={{ color: "var(--text)" }}>
                                        <AIcon size={14} />
                                        <span className="flex-1 truncate">{t(a.labelKey)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <TopbarWhatsApp />

            <NotificationBell />

            <button data-testid="lang-switcher" onClick={cycleLang} title={t("topbar.language") as string} className="inline-flex items-center gap-1 rounded-md px-2.5 text-sm font-medium border transition-colors" style={{ minHeight: 40, minWidth: 44, color: "var(--text)", background: "var(--input)", borderColor: "var(--border)" }}>
                <span className="uppercase text-xs tracking-wider">{currentLangLabel}</span>
            </button>

            <button data-testid="theme-switcher" onClick={cycleTheme} className="inline-flex items-center gap-1.5 rounded-md px-2.5 text-sm border transition-colors" style={{ minHeight: 40, minWidth: 44, color: "var(--text)", background: "var(--input)", borderColor: "var(--border)" }}>
                <ThemeIcon size={16} />
            </button>

            <div ref={profileRef} className="relative">
                <button data-testid="profile-btn" onClick={() => setProfileMenu((v) => !v)} className="rounded-full flex items-center justify-center shrink-0 font-semibold" style={{ width: 36, height: 36, background: "var(--selected-bg)", color: "var(--accent)", fontSize: 13 }}>
                    {(me?.user.full_name || me?.user.email || "?").slice(0, 1).toUpperCase()}
                </button>
                {profileMenu && me && (
                    <div className="absolute right-0 top-11 z-40 rounded-md border shadow-xl min-w-[240px] py-1" style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="profile-menu">
                        <div className="px-3 py-2 border-b" style={{ borderColor: "var(--border)" }}>
                            <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{me.user.full_name || me.user.email}</div>
                            <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{me.user.email}</div>
                            {isPO && <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--accent)" }}><Globe size={12} /> {t("mfa_lock.role.platform_owner")}</div>}
                        </div>
                        {isPO && (
                            <button data-testid="profile-menu-platform" onClick={() => { setProfileMenu(false); navigate("/platform"); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left" style={{ color: "var(--text)" }}>
                                <Globe size={16} /> {t("nav.platform")}
                            </button>
                        )}
                        <button data-testid="profile-menu-security" onClick={() => { setProfileMenu(false); navigate("/settings/security"); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left" style={{ color: "var(--text)" }}>
                            <ShieldCheck size={16} /> {t("nav.security")}
                        </button>
                        <button data-testid="profile-menu-settings" onClick={() => { setProfileMenu(false); navigate("/settings/institution"); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left" style={{ color: "var(--text)" }}>
                            <Settings size={16} /> {t("nav.settings")}
                        </button>
                        <button data-testid="profile-menu-profile" onClick={() => { setProfileMenu(false); navigate("/account"); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left border-t" style={{ color: "var(--text)", borderColor: "var(--border)" }}>
                            <UserCircle2 size={16} /> {t("topbar.profile")}
                        </button>
                        <button data-testid="sign-out-btn" onClick={async () => { setProfileMenu(false); await logout(); navigate("/login", { replace: true }); }} className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left border-t" style={{ color: "var(--error-fg)", borderColor: "var(--border)" }}>
                            <LogOut size={16} /> {t("topbar.sign_out")}
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}
